/**
 * Transform the entry that comes from the Statamic to the
 * entity that we require in Vue.
 *
 * @param entry
 * @returns {{dates, locations: *}}
 */
import {addHours, format, formatISO} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { parseFromISO, formatTime } from "./date";
import {usePage} from '@inertiajs/vue3';

export function hydrateEventEntry(entry) {
    const event = {
        id: entry.id,
        url: entry.url,
        category: entry.event_type?.category,
        locations: entry.locations,
    }

    event.title = (() => {
        if (entry.custom_name?.trim().length) {
            return entry.custom_name.trim();
        }

        return entry.event_type?.title || entry.title;
    })();

    event.description = (() => {
        if (entry.custom_description?.length) {
            const isEmpty = entry.custom_description.every((content) => {
                if (content.type === 'set') {
                    return false;
                }

                if (content.type === 'text' && content.text?.trim().length) {
                    const text = new DOMParser().parseFromString(content.text.trim(), 'text/html');

                    return (text.body.textContent.trim() === '');
                }

                return content.type === 'paragraph' && !content.content.length;
            });

            if (! isEmpty) {
                return entry.custom_description;
            }
        }

        return entry.event_type?.default_description;
    })();

    event.utc = {
        start: entry.start_datetime || null,
        end: entry.end_datetime || null,
    };

    event.dates = (() => {
        function build(date) {
            if (! date) {
                return null;
            }

            return buildParsed(parseFromISO(date));
        }

        function buildParsed(parsed) {
            return {
                dateObj: parsed,
                iso: formatISO(parsed),
                time: formatTime(parsed) || null,
                date: format(parsed, 'eeee, MMMM d, yyyy'),
                date_calendar: format(parsed, "yyyyMMdd'T'HHmm'00'"),
                date_outlook: format(parsed, "yyyy-MM-dd'T'HH:mm:'00'"),
            }
        }

        const start = build(entry.start_datetime);
        let end = build(entry.end_datetime);

        if (end !== null && start.dateObj.getTime() >= end.dateObj.getTime()) {
            end = buildParsed(new Date(start.dateObj.getTime() + (60 * 60 * 1000)))
        }

        return {
            sameDay: start.date === end?.date,
            start,
            end,
        }
    })();

    event.location_description = (() => {
        if (! entry?.locations?.length) {
            return null;
        }

        if (entry.locations.length > 1) {
            return 'Multiple locations';
        }

        return entry.locations[0].short_title;
    })();

    event.links = (() => {
        const startDate = event.dates?.start?.date_calendar;
        const endDate = event.dates?.end?.date_calendar;
        const endDateFallback = startDate && format(addHours(event.dates.start.dateObj, 1), "yyyyMMdd'T'HHmm'00'");
        const endDateForLinks = endDate || endDateFallback;
        const yahooStartUtc = event.utc?.start
            ? formatInTimeZone(new Date(event.utc.start), 'UTC', "yyyyMMdd'T'HHmmss'Z'")
            : null;
        const yahooEndUtc = event.utc?.end
            ? formatInTimeZone(new Date(event.utc.end), 'UTC', "yyyyMMdd'T'HHmmss'Z'")
            : (event.utc?.start ? formatInTimeZone(addHours(new Date(event.utc.start), 1), 'UTC', "yyyyMMdd'T'HHmmss'Z'") : null);
        const startDateOutlook = event.dates?.start?.date_outlook;
        const endDateOutlook = event.dates?.end?.date_outlook;
        const outlookStartUtc = event.utc?.start
            ? formatInTimeZone(new Date(event.utc.start), 'UTC', "yyyy-MM-dd'T'HH:mm:ss'Z'")
            : null;
        const outlookEndUtc = event.utc?.end
            ? formatInTimeZone(new Date(event.utc.end), 'UTC', "yyyy-MM-dd'T'HH:mm:ss'Z'")
            : (event.utc?.start ? formatInTimeZone(addHours(new Date(event.utc.start), 1), 'UTC', "yyyy-MM-dd'T'HH:mm:ss'Z'") : null);

        const googleDates = startDate + (endDateForLinks ? `/${endDateForLinks}` : '');

        const platform = window.navigator?.userAgentData?.platform || window.navigator.platform;
        const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K', 'macOS'];
        const iosPlatforms = ['iPhone', 'iPad', 'iPod', 'iOS', 'iPadOS'];

        const supportsWebCal = (macosPlatforms.indexOf(platform) !== -1) || (iosPlatforms.indexOf(platform) !== -1);
        const isMobile = (iosPlatforms.indexOf(platform) !== -1) || /Android/i.test(window.navigator.userAgent);

        const dtEnd = endDateForLinks ? `%0ADTEND:${endDateForLinks}` : '';
        const calendar = supportsWebCal ?
            `webcal://${window.location.host}/event-download/${event.id}.ics` :
            `data:text/calendar;charset=utf8,%0ABEGIN:VCALENDAR%0AVERSION:2.0%0ABEGIN:VEVENT%0ADTSTART:${startDate}${dtEnd}%0ASUMMARY:${event.title}%0ALOCATION:${event.location_description || ''}%0AEND:VEVENT%0AEND:VCALENDAR`;

        const locationDesc = event.location_description || '';
        const page = usePage();

        const googleBase = page.props.global.calendar.google;

        const googleUrl = (() => {
            if (!googleBase) {
                return null;
            }

            if (googleBase.includes('eventedit')) {
                return 'https://calendar.google.com/calendar/render';
            }

            return googleBase;
        })();

        const googleCalendarLink = (() => {
            if (!googleUrl) {
                return null;
            }

            const url = new URL(googleUrl);
            url.searchParams.set('action', 'TEMPLATE');
            url.searchParams.set('text', event.title);
            url.searchParams.set('dates', googleDates);
            if (locationDesc) {
                url.searchParams.set('location', locationDesc);
            }
            url.searchParams.set('ctz', 'America/Phoenix');

            return url.toString();
        })();

        const outlookLink = (() => {
            const outlookBase = page.props.global.calendar.outlook;
            if (!outlookBase) {
                return null;
            }

            // On mobile devices, use ICS download instead of web URL
            if (isMobile) {
                return `https://${window.location.host}/event-download/${event.id}.ics`;
            }

            const url = new URL(outlookBase);
            url.searchParams.set('body', event.title);
            if (outlookEndUtc) {
                url.searchParams.set('enddt', outlookEndUtc);
            }
            if (locationDesc) {
                url.searchParams.set('location', locationDesc);
            }
            url.searchParams.set('path', '/calendar/action/compose');
            url.searchParams.set('rru', 'addevent');
            url.searchParams.set('startdt', outlookStartUtc || startDateOutlook);
            url.searchParams.set('subject', event.title);

            return url.toString();
        })();

        const yahooLink = (() => {
            const yahooBase = page.props.global.calendar.yahoo;
            if (!yahooBase) {
                return null;
            }

            const url = new URL(yahooBase);
            url.searchParams.set('v', '60');
            url.searchParams.set('view', 'd');
            url.searchParams.set('type', '20');
            url.searchParams.set('title', event.title);
            url.searchParams.set('st', yahooStartUtc || startDate);
            if (yahooEndUtc) {
                url.searchParams.set('et', yahooEndUtc);
            }
            if (locationDesc) {
                url.searchParams.set('in_loc', locationDesc);
            }

            return url.toString();
        })();

        return {
            outlook: outlookLink,
            iCalendar: calendar,
            yahooCalendar: yahooLink,
            googleCalendar: googleCalendarLink,
        }
    })();

    event.thumb_image = (() => {
        if (entry.custom_image?.length) {
            return entry.custom_image[0];
        }

        if (entry.augmented_event_type?.thumb_image?.length) {
            return entry.augmented_event_type.thumb_image[0];
        }

        if (entry.event_type?.default_image?.length) {
            return entry.event_type?.default_image[0];
        }

        return null;
    })();

    event.main_image = (() => {
        if (entry.custom_image?.length) {
            return entry.custom_image[0];
        }

        if (entry.event_type?.default_image?.length) {
            return entry.event_type?.default_image[0];
        }

        if (entry.event_type?.thumb_image?.length) {
            return entry.event_type?.thumb_image[0];
        }

        return null;
    })();

    event.resources = (() => {
        const resources = entry.resources || [];

        return (entry.include_default_event_resources)
            ? resources.concat(entry.event_type?.resources)
            : resources;
    })();

    event.contact = {
        contact: (entry.custom_contact)
            ? entry.custom_contact
            : entry.event_type?.default_contact,
    };

    return event;
}
